// Angular dependencies
import { Injectable } from '@angular/core';

// Excel export dependencies
import { saveAs } from 'file-saver-es';
import { Workbook } from 'exceljs';
import { Observable, take } from 'rxjs';
import { HttpClient, HttpResponse } from '@angular/common/http';
import { AppSettings } from 'src/app/app.config';

const EXCEL_EXTENSION = '.xlsx';

const EXCEL_TYPE = 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;charset=UTF-8';

@Injectable({
  providedIn: 'root'
})
export class ExportExcelService {
  private apiUrl: string;

  constructor(
    private readonly http: HttpClient
  ) {
    this.apiUrl = `${AppSettings.API_PROD_ENDPOINT}data-searcher`;
  }


  public exportAsExcelFile(data: any[], config: any, excelFileName: string, tableFooter?: any[], removeEmptyRows = true): void {


    let filteredData = data;

    if (!data || !data.length || !removeEmptyRows) {
      filteredData = data;
    } else {
      filteredData = this.removeEmptyRows(data);
      if (tableFooter) tableFooter = this.removeEmptyRows(tableFooter);
    }

    // By default, remove empty rows
    // if (config?.noFilterEmptyRows === null || config?.noFilterEmptyRows === undefined || config?.noFilterEmptyRows === false) {
    //   filteredData = this.removeEmptyRows(data);
    //   if (tableFooter) tableFooter = this.removeEmptyRows(tableFooter);
    // } else {
    //   filteredData = data;
    // }

    // Excel data and headers setup
    const rows = [];
    const header = [];
    const headerRows = config.headerRows || 1;
    const footerRows = [];

    // Format headers to column headers
    config.columns.forEach((h) => {
      if (h.childColumns && h.childColumns.length > 0) {
        h.childColumns.forEach((c) => {
          if (c.childColumns && c.childColumns.length > 0) {
            c.childColumns.forEach(() => {
              header.push(h);
            });
          } else {
            header.push(h);
          }
        });
      } else {
        header.push(h);
      }
    });

    // Create workbook and worksheet
    const workbook = new Workbook();
    const worksheet = workbook.addWorksheet(excelFileName, {
      views: [
        // { state: 'frozen', showRuler: false, showGridLines: false, xSplit: headers.fixedColumns || 0, ySplit: headers.headerRows || 1 },
        { showGridLines: false }
      ]
    });

    // Global properties
    worksheet.properties.defaultColWidth = 15;

    // Fixed Global Header Row
    const globalHeaderRow = worksheet.addRow([config.tableTitle || excelFileName]);

    // Add Header Rows
    for (let r = 1; r <= headerRows; r++) {
      let headerRow = [];
      header.forEach((h, i) => {
        let titleProp = 'title';
        if (h.title.template) {
          titleProp = 'textTitle';
        }
        if (r === 1) {
          const master = headerRow.findIndex((item) => item[titleProp] === h[titleProp]);
          const row = { title: h[titleProp], row: r, column: i + 1, master };
          if (headerRow.some((item) => item.master === master)) { row.master = -1; }
          headerRow.push(row);
        } else if (r === 2) {
          if (h.childColumns && h.childColumns.length > 0) {
            h.childColumns.forEach((c, idx) => {
              if (!headerRow.some((item) => item[titleProp] === c[titleProp] && item.parent === h[titleProp])) {
                headerRow.push({ title: c[titleProp], row: r, column: i + 1, parent: h[titleProp], last: idx + 1 >= h.childColumns.length });
              }
            });
          } else {
            headerRow.push({ title: h[titleProp], row: r, column: i + 1 });
          }
        } else if (r === 3) {
          if (h.childColumns && h.childColumns.length > 0) {
            h.childColumns.forEach((c) => {
              if (!headerRow.some((item) => item[titleProp] === c[titleProp] && item.parent === h[titleProp])) {
                if (c.childColumns && c.childColumns.length > 0) {
                  c.childColumns.forEach((g) => {
                    if (!headerRow.some((item) => item[titleProp] === g[titleProp] && item.parent === h[titleProp])) {
                      headerRow.push({ title: g[titleProp], row: r, column: i + 1, parent: h[titleProp] });
                    }
                  });
                } else {
                  headerRow.push({ title: c[titleProp], row: r, column: i + 1, parent: h[titleProp] });
                }
              }
            });
          } else {
            headerRow.push({ title: h[titleProp], row: r, column: i + 1 });
          }
        }
      });

      // Style and merge Header Rows
      if (r > 1) {
        let count: any = worksheet.getRow(2).values.length
        headerRow.length = count - 1
      }

      const tableHeader = worksheet.addRow(headerRow);
      tableHeader.eachCell({ includeEmpty: true }, (cell: any, num: number) => {
        const title = cell.value.title;
        if ('master' in cell.value && cell.value.master >= 0) {
          const a = tableHeader.getCell(cell.value.master + 1);
          const b = tableHeader.getCell(cell.value.column);
          worksheet.mergeCells(a.address, b.address);
          b.alignment = {
            vertical: 'middle',
            horizontal: 'center',
            wrapText: true,
            indent: 0
          };
          b.border = { right: { style: 'thin', color: { argb: 'FFAAAAAA' } } };
        } else {
          cell.border = cell.value.last ? { right: { style: 'thin', color: { argb: 'FFAAAAAA' } } } : {};
          cell.alignment = {
            vertical: 'middle',
            horizontal: cell.value.parent ? 'center' : 'left',
            wrapText: true,
            indent: cell.value.parent ? 0 : 2
          };
        }

        if (r === headerRows && !cell.value.parent) {
          const a = worksheet.getRow(2).getCell(cell.value.column);
          const b = worksheet.getRow(headerRows + 1).getCell(cell.value.column);
          worksheet.mergeCells(a.address, b.address);
          b.border = { right: { style: 'thin', color: { argb: 'FFAAAAAA' } } };
        }
        cell.font = { name: 'Arial', family: 3, size: 8, color: { argb: 'FFFFFFFF' }, bold: true };
        cell.alignment.wrapText = true;
        cell.fill = {
          type: 'pattern',
          pattern: 'solid',
          fgColor: { argb: 'FF808180' },
        };

        cell.value = title;
      });
    }


    // Format data to rows
    filteredData.forEach((r, i) => {
      let value = [];
      if (!config.hasChildrenRow) {
        config.columns.forEach((h, n) => {
          const dataProperty = r[h.dataProperty] ? r[h.dataProperty] : r;
          if (dataProperty.children) {
            h.childColumns.forEach((c, idx) => {
              value.push(
                {
                  title: dataProperty.children[c.dataProperty].data ? dataProperty.children[c.dataProperty].data[c.dataProperty] : dataProperty.children[c.dataProperty],
                  row: i + 1,
                  column: n + 1,
                  parent: c.title,
                  last: idx + 1 >= h.childColumns.length,
                  rowType: r.rowType
                }
              );
            });
          } else {
            value.push(
              {
                title: dataProperty.data ? dataProperty.data[h.dataProperty] : dataProperty,
                row: i + 1,
                column: n + 1,
                rowType: r.rowType
              }
            );
          }
        });
        rows.push(value);
      } else {
        if (r.childrenRows) {
          r.childrenRows.forEach((row, num) => {
            value = []
            config.columns.forEach((h, n) => {
              const dataProperty = r[h.dataProperty] ? r[h.dataProperty] : row[h.dataProperty] || {};
              if (dataProperty.children) {
                h.childColumns.forEach((c, idx) => {
                  value.push(
                    {
                      title: dataProperty.children[c.dataProperty].data ? dataProperty.children[c.dataProperty].data[c.dataProperty] : dataProperty.children[c.dataProperty],
                      row: i + 1,
                      column: n + 1,
                      parent: c.title,
                      last: idx + 1 >= h.childColumns.length,
                      rowType: r.rowType
                    }
                  );
                });
              } else {
                const v = {
                  title: dataProperty.data ? dataProperty.data[h.dataProperty] : dataProperty,
                  row: num + 1,
                  column: n + 1,
                  rowType: r.rowType
                };
                // if (v.column === n + 1) { v['parent'] = true; }
                value.push(v)
              }
            });
            rows.push(value);
          });
        }
      }
    });


    // Add Data and Conditional Formatting
    rows.forEach((d, i) => {
      const row = worksheet.addRow(d);
      if (row.number % 2 === 0) {
        row.eachCell({ includeEmpty: true }, (cell: any) => {
          cell.fill = {
            type: 'pattern',
            pattern: 'solid',
            fgColor: { argb: 'FFFAFAFC' },
          };
        });
      } else {
        row.eachCell({ includeEmpty: true }, (cell: any) => {
          cell.fill = {
            type: 'pattern',
            pattern: 'solid',
            fgColor: { argb: 'FFFFFFFF' },
          };
        });
      }
      row.eachCell({ includeEmpty: true }, (cell: any, num: number) => {

        const type = cell.value.title?.type;
        const title = cell.value.title?.value;

        // const regExp = /[a-zA-Z]/g;
        // if (typeof cell.value.title === 'string' && !regExp.test(cell.value.title)) {
        //   title = typeof cell.value.title === 'string' && !isNaN(parseInt(cell.value.title, 10)) ? cell.value.title.replace(',', '').replace('.', '') : cell.value.title;
        //   title = isNaN(parseInt(title, 10)) ? title : parseInt(title, 10);
        // }


        if (cell.value.rowType === 'info') {
          cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFCCCCCC' } };
        }
        cell.border = {
          right: cell.value.last || !cell.value.parent ? { style: 'thin', color: { argb: 'FF808080' } } : {},
          bottom: { style: 'dotted', color: { argb: 'FF808080' } },
        };
        cell.font = { name: 'Arial', family: 3, size: 8, color: { argb: 'FF2E2E38' } };
        cell.alignment = { vertical: 'middle', horizontal: cell.value.parent ? 'center' : 'left', wrapText: true, indent: cell.value.parent ? 0 : 2 };

        cell.numFmt = type === 'number' ? '#,##0' : '@';
        cell.value = title;
        if (type === 'number') {
          cell.value = this.formatNumber(title);
        } else if (type === 'percentage') {
          cell.value = this.formatPercentage(title);
        }
      });
    });


    if (tableFooter?.length) {
      tableFooter.forEach((r, i) => {
        const value = [];
        config.columns.forEach((h, n) => {
          const dataProperty = r[h.dataProperty] ? r[h.dataProperty] : r;
          if (dataProperty.children) {
            h.childColumns.forEach((c, idx) => {
              value.push(
                {
                  title: dataProperty.children[c.dataProperty],
                  row: i + 1,
                  column: n + 1,
                  parent: c.title,
                  last: idx + 1 >= h.childColumns.length
                }
              );
            });
          } else {
            value.push(
              { title: dataProperty.data ? dataProperty.data[h.dataProperty] : dataProperty, row: i + 1, column: n + 1 }
            );
          }
        });
        footerRows.push(value);
      });
    }

    footerRows.forEach((d, i) => {
      const row = worksheet.addRow(d);
      row.eachCell({ includeEmpty: true }, (cell: any) => {
        cell.fill = {
          type: 'pattern',
          pattern: 'solid',
          fgColor: { argb: 'FF808180' },
        };
      });
      row.eachCell({ includeEmpty: true }, (cell: any, num: number) => {
        const title = typeof cell.value.title === 'string' && !isNaN(parseInt(cell.value.title, 10)) ? cell.value.title.replace(',', '').replace('.', '') : cell.value.title;
        cell.font = { name: 'Arial', family: 3, size: 8, color: { argb: 'FFFFFFFF' } };
        cell.alignment =
          { vertical: 'middle', horizontal: cell.value.parent ? 'center' : 'left', wrapText: true, indent: cell.value.parent ? 0 : 2 };

        cell.value = isNaN(parseInt(title, 10)) ? title : parseInt(title, 10);
      });
    });


    // Fixed Footer Row
    const footerRow = worksheet.addRow(['Source: Information provided during the DD']);

    // Merge and Style Footer Cells
    footerRow.eachCell((cell: any) => {
      cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFFFFFFF' } };
      cell.font = { name: 'Arial Narrow', family: 3, size: 8, color: { argb: 'FF808080' } };
      const a = footerRow.getCell(1);
      const b = footerRow.getCell(worksheet.columns.length);
      worksheet.mergeCells(a.address, b.address);
    });

    // Merge and Style Global Header Row
    globalHeaderRow.eachCell((cell: any) => {
      cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FF333333' } };
      cell.font = { name: 'Arial', family: 3, size: 8, color: { argb: 'FFFFFFFF' }, bold: true };
      const a = worksheet.getRow(1).getCell(1);
      const b = worksheet.getRow(1).getCell(worksheet.actualColumnCount - 1);
      const c = worksheet.getRow(1).getCell(worksheet.actualColumnCount);
      worksheet.mergeCells(a.address, b.address);
      c.value = 'Currency: € 000';
      c.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FF333333' } };
      c.font = { name: 'Arial', family: 3, size: 8, color: { argb: 'FFFFFFFF' }, bold: true };
      c.alignment = { vertical: 'middle', horizontal: 'right' };
    });

    workbook.xlsx.writeBuffer().then((excel) => {
      this.saveAsExcelFile(excel, excelFileName);
    });

  }

  private formatNumber(value): number | string {
    if (value === 'N/A') return value;
    let formattedValue = 0;
    if (value) {
      formattedValue = Math.trunc(value / 1000);
    }
    return formattedValue;
  }

  private formatDefaultNumber(value): number | string {
    if (value === 'N/A') return value;
    let formattedValue = 0;
    if (value) {
      formattedValue = parseFloat(value);
    }
    return formattedValue;
  }

  private formatPercentage(value) {
    if (value === 'N/A') return value;
    let formattedValue = '0';
    if (value) {
      formattedValue = parseFloat(parseFloat(value).toFixed(2)).toLocaleString('en');
    }
    return formattedValue + '%';
  }

  private yearValidation(data): boolean {
    if (typeof data === 'number' && data > 1980 && data <= new Date().getFullYear() + 2) { return true; }
    if (data.length === 4 && data[0] === 1 || data[1]) { return true; }
    return false;
  }

  // REVIEW - funcion to remove row when all type number and type percent columns no data
  removeEmptyRows(data: any): any {
    // Define format functions to format number and then check if has value
    function formatNumber(value): string {
      if (value === 'N/A') return '0';
      let formattedValue = '0';
      if (value) {
        formattedValue = Math.abs(value / 1000).toLocaleString('en', { minimumFractionDigits: 0, maximumFractionDigits: 0 });
      }
      return formattedValue;
    }
    function formatDefaultNumber(value): string {
      if (value === 'N/A') return '0';
      let formattedValue = '0';
      if (value) {
        return value.toLocaleString('en', { minimumFractionDigits: 0, maximumFractionDigits: 2 });
      }
      return formattedValue;
    }

    // Define format function to format percentage and then check if has value
    function formatPercentage(value): string {
      if (value === 'N/A') return '0';
      let formattedValue = '0';
      if (value) {
        formattedValue = parseFloat(parseFloat(value).toFixed(2)).toLocaleString('en');
      }
      return formattedValue;
    }

    function removeRows(row) {
      let hasData = false;
      Object.keys(row).forEach(columnKey => {
        const column = row[columnKey];
        if (column) {
          // if table has childrenRows
          if (columnKey === 'childrenRows') {
            const childrenRows = row[columnKey];
            childrenRows.forEach(childrenRow => {
              Object.keys(childrenRow).forEach(childrenRowColumnKey => {
                const childRowColumn = childrenRow[childrenRowColumnKey];
                if (childRowColumn) {
                  if (!childRowColumn.children) {
                    if (!childRowColumn.data) {
                      if (childRowColumn.type === 'number' && formatNumber(childRowColumn.value) !== '0') {
                        hasData = true;
                      } else if (childRowColumn.type === 'defaultNumber' && formatDefaultNumber(childRowColumn.value) !== '0') {
                        hasData = true;
                      } else if (childRowColumn.type === 'percentage' && formatPercentage(childRowColumn.value) !== '0') {
                        hasData = true;
                      }
                    } else {
                      if (childRowColumn.data[childrenRowColumnKey]?.type === 'number' && formatNumber(childRowColumn.data[childrenRowColumnKey]?.value) !== '0') {
                        hasData = true;
                      } else if (childRowColumn.data[childrenRowColumnKey]?.type === 'defaultNumber' && formatDefaultNumber(childRowColumn.data[childrenRowColumnKey]?.value) !== '0') {
                        hasData = true;
                      } else if (childRowColumn.data[childrenRowColumnKey]?.type === 'percentage' && formatPercentage(childRowColumn.data[childrenRowColumnKey]?.value) !== '0') {
                        hasData = true;
                      }
                    }
                  } else {
                    Object.keys(childRowColumn.children).forEach(childRowchildColumnKey => {
                      const childRowChildColumn = childRowColumn.children[childRowchildColumnKey];
                      if (childRowChildColumn) {
                        if (!childRowChildColumn.children) {
                          if (!childRowChildColumn.data) {
                            if (childRowChildColumn.type === 'number' && formatNumber(childRowChildColumn.value) !== '0') {
                              hasData = true;
                            } else if (childRowChildColumn.type === 'defaultNumber' && formatDefaultNumber(childRowChildColumn.value) !== '0') {
                              hasData = true;
                            } else if (childRowChildColumn.type === 'percentage' && formatPercentage(childRowChildColumn.value) !== '0') {
                              hasData = true;
                            }
                          } else {
                            if (childRowChildColumn.data[childRowchildColumnKey]?.type === 'number' && formatNumber(childRowChildColumn.data[childRowchildColumnKey]?.value) !== '0') {
                              hasData = true;
                            } else if (childRowChildColumn?.data[childRowchildColumnKey]?.type === 'defaultNumber' && formatDefaultNumber(childRowChildColumn.data[childRowchildColumnKey]?.value) !== '0') {
                              hasData = true;
                            } else if (childRowChildColumn?.data[childRowchildColumnKey]?.type === 'percentage' && formatPercentage(childRowChildColumn.data[childRowchildColumnKey]?.value) !== '0') {
                              hasData = true;
                            }
                          }
                        } else {
                          Object.keys(childRowChildColumn.children).forEach(childRowGrandChildColumnKey => {
                            const childRowGrandChildColumn = childRowChildColumn.children[childRowGrandChildColumnKey];
                            if (childRowGrandChildColumn) {
                              if (!childRowGrandChildColumn.data) {
                                if (childRowGrandChildColumn.type === 'number' && formatNumber(childRowGrandChildColumn.value) !== '0') {
                                  hasData = true;
                                } else if (childRowGrandChildColumn.type === 'defaultNumber' && formatDefaultNumber(childRowGrandChildColumn.value) !== '0') {
                                  hasData = true;
                                } else if (childRowGrandChildColumn.type === 'percentage' && formatPercentage(childRowGrandChildColumn.value) !== '0') {
                                  hasData = true;
                                }
                              } else {
                                if (childRowGrandChildColumn.data[childRowGrandChildColumnKey]?.type === 'number' && formatNumber(childRowGrandChildColumn.data[childRowGrandChildColumnKey]?.value) !== '0') {
                                  hasData = true;
                                } else if (childRowGrandChildColumn.data[childRowGrandChildColumnKey]?.type === 'defaultNumber' && formatDefaultNumber(childRowGrandChildColumn.data[childRowGrandChildColumnKey]?.value) !== '0') {
                                  hasData = true;
                                } else if (childRowGrandChildColumn.data[childRowGrandChildColumnKey]?.type === 'percentage' && formatPercentage(childRowGrandChildColumn.data[childRowGrandChildColumnKey]?.value) !== '0') {
                                  hasData = true;
                                }
                              }
                            }
                          })
                        }
                      }
                    })
                  }
                }
              })
            })
          }
          // if table hasnt chidrenRows and hasnt children
          else if (!column.children) {
            if (!column.data) {
              if (column.type === 'number' && formatNumber(column.value) !== '0') {
                hasData = true;
              } else if (column.type === 'defaultNumber' && formatDefaultNumber(column.value) !== '0') {
                hasData = true;
              } else if (column.type === 'percentage' && formatPercentage(column.value) !== '0') {
                hasData = true;
              }
            } else {
              if (column.data[columnKey]?.type === 'number' && formatNumber(column.data[columnKey]?.value) !== '0') {
                hasData = true;
              } else if (column.data[columnKey]?.type === 'defaultNumber' && formatDefaultNumber(column.data[columnKey]?.value) !== '0') {
                hasData = true;
              } else if (column.data[columnKey]?.type === 'percentage' && formatPercentage(column.data[columnKey]?.value) !== '0') {
                hasData = true;
              }
            }
          }
          // if table hasnt chidrenRows but has children
          else {
            Object.keys(column.children).forEach(childColumnKey => {
              const childColumn = column.children[childColumnKey];
              if (childColumn) {
                if (!childColumn.children) {
                  if (!childColumn.data) {
                    if (childColumn.type === 'number' && formatNumber(childColumn.value) !== '0') {
                      hasData = true;
                    } else if (childColumn.type === 'defaultNumber' && formatDefaultNumber(childColumn.value) !== '0') {
                      hasData = true;
                    } else if (childColumn.type === 'percentage' && formatPercentage(childColumn.value) !== '0') {
                      hasData = true;
                    }
                  } else {
                    if (childColumn.data[childColumnKey]?.type === 'number' && formatNumber(childColumn.data[childColumnKey]?.value) !== '0') {
                      hasData = true;
                    } else if (childColumn.data[childColumnKey]?.type === 'defaultNumber' && formatDefaultNumber(childColumn.data[childColumnKey]?.value) !== '0') {
                      hasData = true;
                    } else if (childColumn.data[childColumnKey]?.type === 'percentage' && formatPercentage(childColumn.data[childColumnKey]?.value) !== '0') {
                      hasData = true;
                    }
                  }
                } else {
                  Object.keys(childColumn.children).forEach(grandChildColumnKey => {
                    const grandChildColumn = childColumn.children[grandChildColumnKey];
                    if (grandChildColumn) {
                      if (!grandChildColumn.data) {
                        if (grandChildColumn.type === 'number' && formatNumber(grandChildColumn.value) !== '0') {
                          hasData = true;
                        } else if (grandChildColumn.type === 'defaultNumber' && formatDefaultNumber(grandChildColumn.value) !== '0') {
                          hasData = true;
                        } else if (grandChildColumn.type === 'percentage' && formatPercentage(grandChildColumn.value) !== '0') {
                          hasData = true;
                        }
                      } else {
                        if (grandChildColumn.data[grandChildColumnKey]?.type === 'number' && formatNumber(grandChildColumn.data[grandChildColumnKey]?.value) !== '0') {
                          hasData = true;
                        } else if (grandChildColumn.data[grandChildColumnKey]?.type === 'defaultNumber' && formatDefaultNumber(grandChildColumn.data[grandChildColumnKey]?.value) !== '0') {
                          hasData = true;
                        } else if (grandChildColumn.data[grandChildColumnKey]?.type === 'percentage' && formatPercentage(grandChildColumn.data[grandChildColumnKey]?.value) !== '0') {
                          hasData = true;
                        }
                      }
                    }
                  })
                }
              }
            })
          }
        }
      })
      return hasData;
    }
    const filteredData = data.filter(removeRows);
    return filteredData;
  }

  private saveAsExcelFile(buffer: any, fileName: string): void {
    const data: Blob = new Blob([buffer], { type: EXCEL_TYPE });
    saveAs.saveAs(data, fileName + EXCEL_EXTENSION);
  }

  exportToXslx(exportData: any): Observable<HttpResponse<Blob>> {
    this.apiUrl += '/export';
    return this.http.post(this.apiUrl, exportData, { observe: 'response', responseType: 'blob' }).pipe(take(1));
  }
}
